import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, UserCheck, ShieldAlert, Clock3, RotateCw } from "lucide-react";

import { publicPost } from "@/lib/publicApi";
import { LOCAL_PROFILE } from "@/lib/environment";
import { checkInvitation, resetInviteCheck, forgetInviteToken, redeemErrors, redeemBody, redeemOutcome } from "@/lib/invitation";
import { PublicShell } from "../components/PublicShell";
import { EnvBlocker, CoreGateNotice } from "../components/EnvGate";
import { useCoreCheck } from "../useCoreCheck";
import { V3Columns, V3Field, V3PasswordField, V3Actions, PhotoAside, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { ErrorNote } from "../components/kit";

const until = (iso) => `${new Date(iso).toLocaleString("de-CH", { timeZone: "Europe/Zurich", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr`;
const TONE = { invalid: ["lilac", ShieldAlert, "Einladung nicht nutzbar"], rate: ["notice", Clock3, "Später erneut versuchen"], config: ["notice", ShieldAlert, "Konfigurationsfehler"], connection: ["notice", RotateCw, "Keine Antwort"] };

// Check runs only after the Core identity was verified; StrictMode re-runs share one request.
function useInviteCheck(ready) {
  const [o, setO] = useState({ kind: "pending" });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!ready) return undefined;
    let live = true;
    checkInvitation(publicPost).then((r) => { if (live) setO(r); });
    return () => { live = false; };
  }, [ready, round]);
  const retry = () => { resetInviteCheck(); setO({ kind: "pending" }); setRound((n) => n + 1); };
  return [o, retry];
}

function Notice({ o, onRetry }) {
  const [tone, icon, title] = TONE[o.kind] || TONE.connection;
  return (
    <div data-testid={`invite-${o.kind}`}>
      <StatusBanner tone={tone} icon={icon} title={title} text={o.text} role="alert" testid="invite-notice" />
      <V3Actions>
        {o.kind === "connection" && <button type="button" onClick={onRetry} data-testid="invite-retry" className={V3_PRIMARY}>Erneut prüfen</button>}
        <Link to="/login" data-testid="invite-to-login" className={V3_SECONDARY}>Zur Anmeldung</Link>
      </V3Actions>
    </div>
  );
}

function Created() {
  return (
    <div data-testid="invite-created">
      <StatusBanner tone="mint" icon={UserCheck} title="Konto angelegt" text="Konto angelegt. Die Anmeldung ist nach der Profilprüfung möglich." role="status" testid="invite-created-banner" />
      <DetailList>
        <DetailRow label="Konto" value="Ausstehend · Profilprüfung offen" testid="invite-created-state" />
        <DetailRow label="Anmeldung" value="Erst nach der Freigabe möglich" testid="invite-created-login" />
      </DetailList>
      <V3Actions><Link to="/login" data-testid="invite-created-login-link" className={V3_SECONDARY}>Zur Anmeldung</Link></V3Actions>
    </div>
  );
}

function RedeemForm({ check }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const flight = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  if (done) return <Created />;
  const submit = async (e) => {
    e.preventDefault();
    if (flight.current) return;
    const er = redeemErrors(f, LOCAL_PROFILE);
    setErrors(er); setNote(null);
    if (Object.keys(er).length) return;
    flight.current = true; setBusy(true);
    const o = redeemOutcome(await publicPost("/member/invitations/redeem", redeemBody(f)));
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    if (o.kind === "created") { forgetInviteToken(); setF({ email: "", password: "" }); setDone(true); return; }
    if (o.kind === "password") setErrors({ password: o.text });
    else setNote(o);
  };
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v })); setErrors((x) => ({ ...x, [k]: undefined })); };
  return (
    <form onSubmit={submit} noValidate data-testid="invite-form" aria-busy={busy}>
      <StatusBanner tone="lilac" icon={MailCheck} title={`Hallo ${check.firstName}`} text={`Deine Einladung ist gültig bis ${until(check.expiresAt)}.`} testid="invite-valid" />
      <div className="mt-[18px]">
        <V3Field id="invite-email" type="email" label="E-Mail-Adresse" autoComplete="email" value={f.email} onChange={(e) => set("email")(e.target.value)} error={errors.email}
          hint="Die Adresse, an die die Einladung ging." placeholder="Noch nicht eingegeben" maxLength={400} />
        <V3PasswordField id="invite-password" label="Passwort" value={f.password} onChange={set("password")} error={errors.password} show={show} onShowChange={setShow}
          hint="10 bis 200 Zeichen. Ein längerer Satz ist sicherer als komplizierte Zeichen." />
      </div>
      {note && <ErrorNote testid={`invite-error-${note.kind}`}>{note.text}</ErrorNote>}
      <V3Actions>
        <button type="submit" disabled={busy} data-testid="invite-submit" className={V3_PRIMARY}>{busy ? "Wird gesendet …" : "Konto anlegen"}</button>
        <Link to="/login" data-testid="invite-cancel" className={V3_SECONDARY}>Abbrechen</Link>
      </V3Actions>
      <p className="mt-[14px] text-[13px] text-v3-muted" data-testid="invite-pending-note">Nach dem Anlegen prüft das VISIBILIS-Team dein Profil. Anmelden kannst du dich erst nach der Freigabe.</p>
    </form>
  );
}

function InviteBody({ ready }) {
  const [o, retry] = useInviteCheck(ready);
  if (!ready || o.kind === "pending") return <p role="status" className="text-[14px] text-v3-muted" data-testid="invite-checking">Einladung wird geprüft …</p>;
  return o.kind === "valid" ? <RedeemForm check={o} /> : <Notice o={o} onRetry={retry} />;
}

// Public page /einladung#token=…; the fragment was taken and removed before the router started (index.js).
export default function Invitation() {
  const gate = useCoreCheck();
  return (
    <PublicShell title="Einladung annehmen" testid="public-invitation" intro="Lege mit deiner Einladung ein Konto an. Die Anmeldung ist erst nach der Profilprüfung möglich.">
      <V3Columns layout="form" aside={<PhotoAside icon={MailCheck} />} main={
        <div data-testid="invite-main">
          {gate.status === "blocked" ? <EnvBlocker problems={gate.problems} title="Einladung gesperrt" note="Der Einladungscode wird nicht gesendet." />
            : <><CoreGateNotice gate={gate} what="Die Prüfung der Einladung" /><InviteBody ready={gate.status === "ok"} /></>}
        </div>
      } />
    </PublicShell>
  );
}
